import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bot, Loader2, Plus, Search, Settings2, Terminal, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

// Panel admin > Bots Discord : tous les bots (gestion et radio) des clients et de VantaPulse,
// avec leur propriétaire et leurs serveurs. Les administrateurs du site ouvrent le tableau
// de bord ou la console de n'importe lequel, avec les mêmes droits que l'acheteur.
// Les bots de base (VantaPulse, VantaPulse Radio…), lancés sur l'hébergement des bots, ne
// sont achetés par personne : on les ajoute ici avec leur token pour les ouvrir pareil.

const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

export default function AllBotsPanel({ hdr }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async (search = "") => {
    try {
      const r = await axios.get(`${API}/dash/admin/bots`, { headers: hdr(), params: { q: search } });
      setData(r.data);
    } catch (err) { toast.error(errorText(err, "Chargement des bots impossible")); }
  }, [hdr]);
  useEffect(() => { load(""); }, [load]);

  const remove = async (b) => {
    if (!window.confirm(`Retirer « ${b.bot_name} » du panel ? Le bot continue de tourner sur l'hébergement.`)) return;
    try {
      await axios.delete(`${API}/dash/admin/system-bots/${b.id}`, { headers: hdr() });
      toast.success("Bot de base retiré du panel");
      load(q);
    } catch (err) { toast.error(errorText(err, "Retrait impossible")); }
  };

  if (!data) return <div className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;
  return (
    <div className="space-y-4" data-testid="admin-all-bots">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-white/60 mr-auto">{data.total} bot{data.total > 1 ? "s" : ""} Discord. Tu peux ouvrir et régler n'importe lequel, comme son acheteur.</p>
        <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bot, client, serveur…" className="pl-8 h-9 w-60" data-testid="admin-all-bots-search" />
        </form>
      </div>
      <AddBaseBot hdr={hdr} onAdded={() => load(q)} />
      {data.bots.length === 0 ? <p className="text-sm text-white/40">Aucun bot.</p> : (
        <div className="divide-y divide-white/5 border border-white/10 rounded-lg">
          {data.bots.map((b) => (
            <div key={b.id} className="px-3 py-3 flex flex-wrap items-center gap-3" data-testid="admin-bot-row">
              {b.bot_avatar_url ? <img src={b.bot_avatar_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                : <span className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0"><Bot className="w-5 h-5 text-white/50" /></span>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                  {b.bot_name}
                  <span className={`w-2 h-2 rounded-full ${b.online ? "bg-emerald-400" : "bg-white/25"}`} title={b.online ? "En ligne" : "Hors ligne"} />
                  <span className="text-[11px] px-1.5 py-0.5 rounded border border-white/10 text-white/50">{b.kind === "radio" ? "Radio" : "Gestion"}</span>
                  {b.system && <span className="text-[11px] px-1.5 py-0.5 rounded border border-violet-500/40 text-violet-200">Bot de base</span>}
                  {b.mine && <span className="text-[11px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-200">À toi</span>}
                  {b.token_invalid && <span className="text-[11px] px-1.5 py-0.5 rounded border border-red-500/40 text-red-200">Token refusé</span>}
                </p>
                <p className="text-xs text-white/45 truncate">
                  {b.system ? "Bot de base VantaPulse" : b.owner_email || "Propriétaire inconnu"}{b.guilds.length ? ` · ${b.guilds.map((g) => g.name).join(", ")}` : " · sur aucun serveur"}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/bot-dashboard/${b.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90"><Settings2 className="w-4 h-4" />Tableau de bord</Link>
                <Link to={`/bot-console/${b.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-sm hover:bg-white/5"><Terminal className="w-4 h-4" />Console</Link>
                {b.system && <button type="button" onClick={() => remove(b)} title="Retirer du panel" data-testid="remove-base-bot"
                  className="inline-flex items-center px-2 py-1.5 rounded-lg border border-white/15 text-white/60 hover:text-red-300 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddBaseBot({ hdr, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ discord_token: "", bot_type: "gestion", use_site_token: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dash/admin/system-bots`, form, { headers: hdr() });
      toast.success(`${r.data.bot.bot_name} ajouté. Son tableau de bord se relie au bot d'ici 10 minutes.`);
      setForm({ discord_token: "", bot_type: "gestion", use_site_token: false });
      setOpen(false);
      onAdded();
    } catch (err) { toast.error(errorText(err, "Ajout impossible")); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="add-base-bot"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-500/40 text-violet-200 text-sm hover:bg-violet-500/10">
        <Plus className="w-4 h-4" />Ajouter un bot de base (VantaPulse, Radio…)
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="border border-violet-500/30 bg-violet-500/5 rounded-lg p-4 space-y-3" data-testid="add-base-bot-form">
      <p className="text-sm text-white/70">
        Pour les bots de base lancés sur l'hébergement des bots (YorkHost). Le token reste sur le serveur du site,
        il n'est jamais réaffiché. Le bot n'est pas lancé une 2e fois.
      </p>
      <div className="flex flex-wrap gap-2">
        {[["gestion", "Gestion (VantaPulse)"], ["radio", "Radio (VantaPulse Radio)"]].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setForm({ ...form, bot_type: v })}
            className={`px-3 py-1.5 rounded-lg text-sm border ${form.bot_type === v ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:bg-white/5"}`}>{label}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-white/70">
        <input type="checkbox" checked={form.use_site_token} onChange={(e) => setForm({ ...form, use_site_token: e.target.checked })} data-testid="base-bot-site-token" />
        Utiliser le bot déjà configuré sur le serveur du site (celui qui envoie les codes Discord)
      </label>
      {!form.use_site_token && (
        <Input type="password" autoComplete="off" value={form.discord_token} onChange={(e) => setForm({ ...form, discord_token: e.target.value })}
          placeholder="Token du bot (Discord Developer Portal > Bot)" data-testid="base-bot-token" />
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || (!form.use_site_token && !form.discord_token.trim())}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Ajouter
        </button>
        <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-lg border border-white/15 text-sm hover:bg-white/5">Annuler</button>
      </div>
    </form>
  );
}
